// Reusable WYSIWYG formatting toolbar for contenteditable editors.
// Uses document.execCommand for bold, italic, strike, underline, code, link, lists.
// Usage: WysiwygToolbar: {} — place near a contenteditable element.

// smbls 3.14.x — FA-L11 fix landed; top-level `title` + `aria: { label }`
// now reach the <button>. No more `attr: { ... }` wrapper needed for
// icon-only buttons.
// `value` is an optional second argument to execCommand — required for
// `formatBlock` (e.g. 'blockquote', 'h1') but absent for toggle commands
// like 'bold', 'insertUnorderedList', etc. (#2193)
//
// `titleKey` is a LITERAL bundle key at every call site; the tooltip and the
// accessible name are one word, resolved where they are painted.
const _btn = (icon, titleKey, cmd, value) => ({
  extends: 'NavbarButton',
  padding: 'Y2',
  title: (el) => el.call('polyglot', titleKey),
  aria: { label: (el) => el.call('polyglot', titleKey) },
  color: 'caption',
  ':hover': { color: 'title', background: 'hover' },
  Icon: { name: icon },
  onMouseDown: (e) => {
    e.preventDefault()
    e.stopPropagation()
    document.execCommand(cmd, false, value || null)
  },
  onClick: (e) => e.preventDefault()
})

export const WysiwygToolbar = {
  flow: 'x',
  gap: 'V',
  align: 'center left',

  Bold: _btn('bold', 'workspace.wysiwyg.bold', 'bold'),
  Italic: _btn('italic', 'workspace.wysiwyg.italic', 'italic'),
  Strike: _btn('strikethrough', 'workspace.wysiwyg.strikethrough', 'strikeThrough'),
  Underline: _btn('underline', 'workspace.wysiwyg.underline', 'underline'),

  Code: {
    extends: 'NavbarButton',
    padding: 'Y2',
    title: (el) => el.call('polyglot', 'workspace.wysiwyg.code'),
    'aria-label': (el) => el.call('polyglot', 'workspace.wysiwyg.code'),
    color: 'caption',
    ':hover': { color: 'title', background: 'hover' },
    Icon: { name: 'code' },
    onMouseDown: (e) => {
      e.preventDefault()
      e.stopPropagation()
      const sel = window.getSelection()
      if (!sel || !sel.rangeCount || sel.isCollapsed) return
      try {
        const code = document.createElement('code')
        code.style.cssText =
          'font-family:monospace;background:rgba(255,255,255,0.08);padding:1px 4px;border-radius:3px'
        sel.getRangeAt(0).surroundContents(code)
        sel.collapseToEnd()
      } catch {}
    },
    onClick: (e) => e.preventDefault()
  },

  Link: {
    extends: 'NavbarButton',
    padding: 'Y2',
    title: (el) => el.call('polyglot', 'workspace.wysiwyg.link'),
    'aria-label': (el) => el.call('polyglot', 'workspace.wysiwyg.link'),
    color: 'caption',
    ':hover': { color: 'title', background: 'hover' },
    Icon: { name: 'link' },
    onMouseDown: (e) => {
      e.preventDefault()
      e.stopPropagation()
      const sel = window.getSelection()
      const saved = sel?.rangeCount ? sel.getRangeAt(0).cloneRange() : null
      const url = prompt('URL:')
      if (!url) return
      if (saved) {
        sel.removeAllRanges()
        sel.addRange(saved)
      }
      document.execCommand('createLink', false, url)
    },
    onClick: (e) => e.preventDefault()
  },

  Quote: _btn('quote', 'workspace.wysiwyg.blockquote', 'formatBlock', 'blockquote'),
  BulletList: _btn('listBulleted', 'workspace.wysiwyg.bulletList', 'insertUnorderedList'),
  NumberList: _btn('listNumbered', 'workspace.wysiwyg.numberedList', 'insertOrderedList'),
  Divider: _btn('minus', 'workspace.wysiwyg.divider', 'insertHorizontalRule')
}
